import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { getAllFunnels } from 'API/linkedin-funnel';
import { ProductGroups } from 'enums';
import { Funnel, FunnelStatuses, FunnelTypes } from 'features/co-pilot/co-pilot.types';
import { OptionType } from 'features/ui/base-components/pai-auto-complete';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiSearchSelector from 'features/ui/base-components/pai-search-selector';
import { useUserData } from 'hooks/use-user-data';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddToFunnelResponse } from 'services/lead-list-research.service';
import { makeStyles } from 'tss-react/mui';
import LeadsTable from './leads-table';

interface AddLeadListToFunnelDialogProps {
  open: boolean;
  onClose: () => void;
  leadListId: string | null;
  leadListName: string;
  onAddToFunnel: (funnelId: string, contactIndices?: number[]) => Promise<AddToFunnelResponse | null>;
  productGroup?: ProductGroups;
  leads?: any[]; // Pass leads for selection
}

const AddLeadListToFunnelDialog: React.FC<AddLeadListToFunnelDialogProps> = ({
  open,
  onClose,
  leadListId,
  leadListName,
  onAddToFunnel,
  productGroup = ProductGroups.LEAD_BOOSTER,
  leads = [],
}) => {
  const { classes } = useStyles();
  const { token } = useUserData();
  const { t } = useTranslation();

  const [selectedFunnelId, setSelectedFunnelId] = useState<string>('');
  const [funnelSearchTerm, setFunnelSearchTerm] = useState<string>('');
  const [displayedFunnelName, setDisplayedFunnelName] = useState<string>('');
  const [campaignOptions, setCampaignOptions] = useState<OptionType[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectionMode, setSelectionMode] = useState<'all' | 'select'>('all');
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);

  const funnelType = productGroup === ProductGroups.HR_BOOSTER ? FunnelTypes.ACTIVE_SOURCING_CO_PILOT : FunnelTypes.SOCIAL_SELLING_CO_PILOT;

  const debouncedFetchFunnels = useCallback(
    _.debounce(async (searchTerm: string) => {
      if (!token) return;
      setLoadingCampaigns(true);
      setErrorMessage(null);
      try {
        const res = await getAllFunnels(funnelType, searchTerm, undefined, undefined, undefined, undefined, undefined, false);
        if (res && !res.error) {
          const resCampaigns: Funnel[] = res.campaigns || [];
          const filteredCampaigns = resCampaigns.filter((campaign) => campaign.status !== FunnelStatuses.ARCHIVED);
          const mappedOptions: OptionType[] = filteredCampaigns.map((funnelItem) => ({
            value: funnelItem._id,
            label: funnelItem.name,
          }));
          setCampaignOptions(mappedOptions);
          if (mappedOptions.length === 0 && !searchTerm) {
            setErrorMessage('No suitable funnels found to add contacts to.');
          }
        } else {
          setCampaignOptions([]);
          setErrorMessage(res.error?.message || 'Failed to fetch funnels.');
        }
      } catch (error: any) {
        console.error('Error fetching funnels:', error);
        setCampaignOptions([]);
        setErrorMessage(error.message || 'An unexpected error occurred while fetching funnels.');
      } finally {
        setLoadingCampaigns(false);
      }
    }, 500),
    [token, funnelType]
  );

  useEffect(() => {
    if (open) {
      debouncedFetchFunnels(funnelSearchTerm);
    } else {
      setSelectedFunnelId('');
      setFunnelSearchTerm('');
      setDisplayedFunnelName('');
      setCampaignOptions([]);
      setErrorMessage(null);
      setSuccessMessage(null);
      setIsProcessing(false);
    }
  }, [open, debouncedFetchFunnels]);

  useEffect(() => {
    if (open && token) {
      debouncedFetchFunnels(funnelSearchTerm);
    }
  }, [funnelSearchTerm, open, token, debouncedFetchFunnels]);

  const handleFunnelSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFunnelSearchTerm(event.target.value);
    setDisplayedFunnelName(event.target.value);
    setSelectedFunnelId('');
  };

  const handleFunnelSelect = (funnelId: string) => {
    return () => {
      const selectedOption = campaignOptions.find((opt) => opt.value === funnelId);
      setSelectedFunnelId(funnelId);
      if (selectedOption) {
        setFunnelSearchTerm(selectedOption.label);
        setDisplayedFunnelName(selectedOption.label);
      }
      setCampaignOptions([]);
    };
  };

  const handleSelectModeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectionMode(e.target.value as 'all' | 'select');
    setSelectedIndices([]);
  };

  const handleRowSelect = (index: number) => {
    setSelectedIndices((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const handleConfirmAdd = async () => {
    if (!leadListId) {
      setErrorMessage('Lead List ID is missing.');
      return;
    }
    if (!selectedFunnelId) {
      setErrorMessage('Please select a funnel from the dropdown.');
      return;
    }
    setIsProcessing(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    try {
      // Map selected indices (based on filtered leads) back to their original indices in the full lead list
      let contactIndicesToSend: number[] | undefined = undefined;
      if (selectionMode === 'select') {
        const originalIndicesOfValidLeads = leads.reduce<number[]>((acc, lead, idx) => {
          if (
            (lead.contactLinkedinUrl && lead.contactLinkedinUrl.startsWith('http')) ||
            (lead.contact?.linkedinUrl && lead.contact.linkedinUrl.startsWith('http'))
          ) {
            acc.push(idx);
          }
          return acc;
        }, []);
        contactIndicesToSend = selectedIndices
          .map((displayIdx) => originalIndicesOfValidLeads[displayIdx])
          .filter((idx): idx is number => idx !== undefined);
      }

      const result = await onAddToFunnel(
        selectedFunnelId,
        contactIndicesToSend
      );
      if (result && result.success) {
        const count = result.funnelUpdateStatus?.countAdded || 0;
        if (count > 0) {
          setSuccessMessage(`${count} contact(s) successfully added to the funnel. You can close this window.`);
        } else {
          setSuccessMessage(
            result.message || 'No new contacts were added to the funnel (they may already exist or the list was empty). You can close this window.'
          );
        }
      } else {
        setErrorMessage(result?.message || 'Failed to add contacts to the funnel.');
      }
    } catch (error: any) {
      console.error('Error in onAddToFunnel:', error);
      setErrorMessage(error.message || 'An unexpected error occurred.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDialogClose = () => {
    if (isProcessing) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth classes={{ paper: classes.paper }}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('addContactsToCampaign')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={1}>
          {successMessage && (
            <Alert className={classes.alert} severity="success" onClose={() => setSuccessMessage(null)}>
              {successMessage}
            </Alert>
          )}
          {errorMessage && (
            <Alert className={classes.alert} severity="error" onClose={() => setErrorMessage(null)}>
              {errorMessage}
            </Alert>
          )}

          {!successMessage && (
            <Stack gap={2}>
              <Typography className={classes.text}>
                {t('addContactsToCampaignDescription', { leadListId: leadListName || leadListId || 'N/A' })}
              </Typography>

              <PaiSearchSelector
                value={displayedFunnelName}
                options={campaignOptions}
                placeholder={t('selectCampaignToAddContacts')}
                loading={loadingCampaigns}
                disabled={isProcessing || !!successMessage}
                onChange={handleFunnelSearchChange}
                onMenuItemClick={handleFunnelSelect}
              />

              {/* Selection mode radio */}
              <Stack direction="row" gap={2} alignItems="center">
                <label>
                  <input
                    type="radio"
                    value="all"
                    checked={selectionMode === 'all'}
                    onChange={handleSelectModeChange}
                    disabled={isProcessing}
                  />
                  {t('sendAllContacts')}
                </label>
                <label>
                  <input
                    type="radio"
                    value="select"
                    checked={selectionMode === 'select'}
                    onChange={handleSelectModeChange}
                    disabled={isProcessing}
                  />
                  {t('selectContactsToSend')}
                </label>
              </Stack>

              {/* Show leads table for selection */}
              {selectionMode === 'select' && leads && leads.length > 0 && (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {t('onlyContactsWithLinkedinUrlCanBeSelected', 'You can only select contacts with a valid LinkedIn URL.')}
                  </Alert>
                  <LeadsTable
                    leads={leads.filter(
                      (item: any) =>
                        (item.contactLinkedinUrl && item.contactLinkedinUrl.startsWith('http')) ||
                        (item.contact?.linkedinUrl && item.contact.linkedinUrl.startsWith('http'))
                    )}
                    selectionMode={true}
                    selectedIndices={selectedIndices}
                    onSelect={handleRowSelect}
                  />
                </>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'md'} variant={'default'} underline={'always'} action={handleDialogClose} disabled={isProcessing} />
        {!successMessage && (
          <PaiButton
            text={selectionMode === 'select' ? t('addSelectedContacts') : t('addContacts')}
            size={'small'}
            border={'rounded'}
            action={handleConfirmAdd}
            disabled={
              loadingCampaigns ||
              !selectedFunnelId ||
              (selectionMode === 'select' && selectedIndices.length === 0)
            }
            loading={isProcessing}
          />
        )}
        {successMessage && <PaiButton text={t('close')} size={'small'} border={'rounded'} action={handleDialogClose} />}
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
    whiteSpace: 'pre-wrap',
    '&.highlight': {
      fontWeight: 'bold',
      color: theme.palette.primary.main,
    },
  },
  alert: {
    margin: '0 !important',
    borderRadius: '8px',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default AddLeadListToFunnelDialog;
